import { debounce, debounced, normalizeFormData, parameterizeUrl, tryConvertToUrl, waitForElement } from "$lib/core";
import { browser } from "$lib/core/js/util/env";
import { Logger } from "$lib/core/js/util/logger";
import { initForms, BuValidationService, fetchField, loadOptions, validationServiceInstance } from "./ui";
import { addRestructureObserver, getNamedControls, indicator2Bool, isCheckbox, loadField, updateYesNoSelect } from "./ui/forms/core";
import { policy } from "$lib/core/js/util/trustedTypes";

export const globalLogger = new Logger("global");

/**
 * @implements {EventTarget}
 */
export class Emitter {
  constructor() {
    const delegate = document.createDocumentFragment();
		this.addEventListener = /** @type {import("./ui/public").EventListenerDelegate} */ (type, callback, options) => delegate.addEventListener(type, callback, options);
		this.dispatchEvent = (/** @type {Event} */ ev) => delegate.dispatchEvent(ev);
		this.removeEventListener = /** @type {import("./ui/public").EventListenerDelegate} */ (type, callback, options) => delegate.removeEventListener(type, callback, options);
  }
}

export class ElementScope extends Emitter {
	/** @type {string} */
	name;
	/** @type {import("./public").IMountable[]} */
	components = [];
	/** @type {Set<HTMLElement>} */
	elements = new Set();
	/** @type {Map<HTMLElement, import("./public").IMountable[]>} */
	componentsByElement = new Map();

	/**
	 * @param {string} name 
	 */
	constructor(name = "") {
		super();
		this.name = name;
	}

	get ready() {
		return this.components.every(c => c.ready ?? true);
	}

	emitIfReady() {
		if (this.ready) {
			this.dispatchEvent(new CustomEvent("ready"));
		}
	}

	/**
	 * @param {import("./public").IMountable} component 
	 */
	add(component) {
		this.components.push(component);
		const t = component.target;
		if (t) {
			this.elements.add(t);
		}
	}

	/**
	 * @template {import("./public").IMountable} T
	 * @param {HTMLElement} element
	 * @returns {T}
	 */
	getComponentFor(element) {
		return /** @type {T} */ (this.componentsByElement.get(element)?.[0] ?? null);
	}

	/**
	 * @template {import("./public").IMountable} T
	 * @param {HTMLElement} element
	 * @returns {T[]}
	 */
	getAllComponentsFor(element) {
		return /** @type {T[]} */ (this.componentsByElement.get(element) ?? []);
	}
}

export class BuFormApi {
	constructor() {
		/** @type {(form: HTMLFormElement, fd?: HTMLElement|FormData|undefined, keepDisabled?: boolean|undefined) => Record<string, (FormDataEntryValue|boolean)|(FormDataEntryValue|boolean)[]>} */
		this.normalizeFormData = normalizeFormData;
		/** @type {(element: HTMLElement, url: import("$lib/core/js/util/defs").UrlLike, init?: import("./ui/forms/dataLoad/public").DataLoadRequestInit | undefined) => Promise<any>} */
		this.fetchField = fetchField;
		/** @type {(element: HTMLElement, data: import("./ui/public").SelectListItem[], type: "checkboxes" | "select" | "autocomplete" | string) => void} */
		this.loadOptions = loadOptions;
		/** @type {(item: any) => item is HTMLInputElement} */
		this.isCheckbox = isCheckbox;
		/** @type {(value: string) => boolean|undefined} */
		this.indicator2Bool = indicator2Bool;
		/** @type {(select: HTMLSelectElement, value: any) => void} */
		this.updateYesNoSelect = updateYesNoSelect;
		/** @type {(form: HTMLFormElement, name: string, filter: (((control: import("$lib/core/js/util/defs").FormControl) => boolean)|null)) => (HTMLElement & {value: string, disabled: boolean, type?: string, checked?: boolean})[]} */
		this.getNamedControls = getNamedControls;
		/** @type {(form: HTMLFormElement, target: HTMLElement|string, value: any, type: string|null, filter: (((control: import("$lib/core/js/util/defs").FormControl) => boolean)|null)) => void} */
		this.loadField = loadField;
		/** @type {BuValidationService} */
		this.validationService = validationServiceInstance;
		/** @type {(formSelector: string|null) => void} */
		this.initForms = initForms;
	}
}

export class BuUtilApi {
	construct() {
		/** @type {(func: () => void, wait: number|string, options: import("$lib/core/js/util/defs").DebounceOptions) => void} */
		this.debounce = debounce;
		/** @template {(...args: any[]) => any} T @type {(func: T, wait: number|string, options: import("$lib/core/js/util/defs").DebounceOptions) => T} */
		this.debounced = debounced;
		/** @type {(url: string, args?: (Record<string, any> | FormData), delimeter?: string, form?: HTMLFormElement) => {url: string, args: Record<string, any>}} */
		this.parameterizeUrl = parameterizeUrl;
		/** @template {Element} TElement @type {(selector: string) => Promise<TElement|null>} */
		this.waitForElement = waitForElement;
	}
}

/**
 * The public, client-side interface for using Baylor UI's JS features from browser code.
 * 
 * Note: `baylor-ui-core.js` is linked in an HTML file using `<script type="module" ...></script>`.
 * This file is responsible for attaching a `BuApi` instance to the `window.BU` global variable.
 * 
 * Because the ES module loading process is deferred by default, it will not actually 
 * attach until just before the `DOMContentLoaded` event is triggered. As such,
 * adding the `ready` function to this public interface is moot.
 */
export class BuApi {
	constructor() {
		/** @type {Record<string, (new (...args: any[]) => any)>} */
		this.types = {};
		/** @type {Record<string, ElementScope>} */
		this.scopes = {
			default: new ElementScope(),
		};
		/** @type {(url: string, args?: (Record<string, any> | FormData), delimeter?: string) => {url: string, args: Record<string, any>}} @deprecated Use `BU.util.parameterizeUrl` instead. */
		this.parameterizeUrl = parameterizeUrl;
		/** @template {Element} TElement @type {(selector: string) => Promise<TElement|null>} @deprecated Use `BU.util.waitForElement` instead. */
		this.waitForElement = waitForElement;
		/** @type {typeof Logger} */
		this.Logger = Logger;
		/** @type {BuFormApi} */
		this.forms = new BuFormApi();
		/** @type {import("../core/js/util/defs").TrustedTypePolicy} */
		this.policy = policy;
		/** @type {BuUtilApi} */
		this.util = new BuUtilApi();
	}
}

/**
 * @type {BuApi} The singleton instance of the public browser `BU` interface.
 */
export const BU = new BuApi();

/**
 * Calls a function when the page finishes loading or immediately if it is already done loading.
 * 
 * @param {(ev?: Event) => any} fn A possibly async callback.
 */
export function ready(fn) {
  if (document?.readyState !== 'loading') {
    fn();
  } else {
    document.addEventListener('DOMContentLoaded', fn);
  }
}

/**
 * 
 * @param {string} url 
 * @param {string | null | undefined} templateId 
 * @returns {Promise<HTMLTemplateElement>}
 */
export async function loadRemoteTemplate(url, templateId) {

	/** @type {Document?} */
	let remoteDoc = null;
	try {
		const response = await fetch(url);
		const text = await response.text();

		const parser = new DOMParser();
		remoteDoc = parser.parseFromString(text, "text/html");
	} catch (err) {
		globalLogger.error(`${loadRemoteTemplate.name}: Failed to load template!`, {
			url,
			templateId,
			message: err,
		});
	}

	const template = /** @type {HTMLTemplateElement} */ (templateId ? remoteDoc?.getElementById(templateId) : remoteDoc?.querySelector("template"));
	if (!template) {
		globalLogger.warn(`No '${templateId}' template at path: `, url);
	}

	return template;
}

/**
 * Emits an "templateloaded" event to indicate that the object has finished loading.
 * This occurs before the "elementmounted" event and only triggers for web pages
 * that opt-in to asynchronous loading of templates.
 * 
 * @template {object} T
 * @param {(new () => T) | string} type
 * @param {string} url The remote location from which to load an HTML template.
 * @param {string | null | undefined} templateId The id of the HTMLTemplate element loaded at `url`.
 * @param {object} element The new "root" element after template creation.
 * @param {object?} original The original target element prior to async loading, if applicable. Otherwise null.
 * @emits "templateloaded" If the function completes successfully. Emitted by the `Window`.
 */
export function notifyLoaded(type, url, templateId, element, original) {
	const ev = new CustomEvent("templateloaded", { detail: {
		type,
		url,
		templateId,
		element,
		original,
	}});
	window.dispatchEvent(ev);
}

/**
 * Emits an "elementmounted" event to indicate that the object has finished initializing itself.
 * This should be called after an element is attached to the document and its `init()` function called.
 * 
 * @template {object} T
 * @param {new () => T} type
 * @param {object} element The element that has been added to the DOM tree and completed initialization.
 */
export function notifyMounted(type, element) {
	const ev = new CustomEvent("elementmounted", { detail: {
		type,
		element,
	}});
	window.dispatchEvent(ev);
}

/**
 * @param {Element} target The target element into which the source element will be merged. It's attributes and classes will be overwritten.
 * @param {Element} source The source element that will copy its attributes, classes, and children onto the target.
 * @param {import("./public").MergeElementOptions} options The options to configure the merge process between elements.
 */
export function mergeElement(target, source, options) {
	const {
		children,
		preserveChildren,
		classes,
		attributes
	} = options;
	if (children && !preserveChildren) {
		// clear children
		for (let child of target.childNodes) {
			child.remove();
		}
	}

	// merge attributes
	if (attributes) {
		for (let name of source.getAttributeNames()) {
			if (name === "class" && classes) continue;
			target.setAttribute(name, source.getAttribute(name) ?? "");
		}
	}

	// merge classes
	if (classes) {
		for (let token of source.classList) {
			if (!target.classList.contains(token)) {
				target.classList.add(token);
			}
		}
	}

	// create & append new children
	if (children) {
		for (let child of source.childNodes) {
			target.appendChild(child);
		}
	}
}

/**
 * @template {object} T
 * @param {(new () => T)|string} type 
 * @param {boolean} [root=false] If true, the `bu-defaults` class will be included to apply normalizing preflight styles to the DOM subtree.
 */
export function getDefaultElementTargetSelector(type, root = false) {
	return `${root ? ".bu-defaults" : ""}[data-is='${typeof(type) === "string" ? type : type.name}']`;
}

Logger.addLogger("loadRemoteElement");

/**
 * Asynchronously loads the footer from a centrally maintained template. Does NOT initialize the footer.
 * Recommended that you call `initFooter()` after this Promise completes.
 * 
 * @template {object} T
 * @param {Partial<import("./public").RemoteLoadTemplateContext<T>>} context
 * @returns {Promise<HTMLElement>} The primary/"root" element from the template's cloned & imported content.
 * @emits "templateloaded" If the function completes successfully. Emitted by the `Window`.
 */
export async function loadRemoteElement(context) {
	const logger = new Logger("loadRemoteElement");

	let {
		type,
		root,
		target,
		prepend,
		head,
		url,
		templateId,
		selector
	} = context;

	if (!type) {
		throw new Error("Cannot load remote element: no type specified!")
	}

	if (!selector) {
		selector = getDefaultElementTargetSelector(type, root);
	}

	if (!url) {
		throw new Error("Cannot load remote element: no url specified!");
	}

	const template = await loadRemoteTemplate(url, templateId);
	
	const content = document.importNode(template.content, true);

	const anchor = /** @type {HTMLElement} */ (target ?? document.querySelector(selector));
	const parent = anchor?.parentNode;
	const original = parent ? anchor : null;
	logger.debug({
		anchor,
		parent,
		original,
		selector
	});
	/** @type {HTMLElement?} */
	let element = null;
	if (!anchor) {
		logger.debug("Inserting template content to document");
		const anchor = head ? document.head : document.body;
		const op = prepend ? anchor.prepend : anchor.append;
		op(template.content);
		logger.debug("Loading element with selector: ", selector);
		element = document.querySelector(selector);
	} else {
		if (!parent) {
			throw new Error(`No parent found for anchor when loading remote element ${type}`);
		}
		const classes = [...anchor.classList];
		const elm = content.firstElementChild;
		parent.replaceChild(content, anchor);
		if (elm != null) {
			classes.filter(token => token !== "bu-defaults" && !elm.classList.contains(token)).forEach(token => {
				elm.classList.add(token);
			});
		}
		const sel = `:scope ${selector}`;
		logger.debug("Loading element with selector from parent: ", parent, sel);
		element = parent.querySelector(sel);
	}

	if (!element) {
		throw new Error("Failed to load element");
	}

	logger.debug("Loaded element", element);

	notifyLoaded(type, url, templateId, element, original);

	return element;
}

/**
 * @template {HTMLElement} T
 * @param {import("./public").MonitorOptions<T>} options 
 * @returns {Promise<MutationObserver?>}
 */
export async function monitor(options) {
	const { target, predicate, callback } = options;
	let { selector } = options;
	
	/** @type {import("./public").MonitorSet<T>} */
	let set = {
		elements: [],
		added: [],
		removed: [],
	}

	const root = /** @type {ParentNode} */ (target ?? document);
	const targetSelector = selector && target ? ":scope " + selector : selector;

	/** @type {MutationObserver?} */
	let observer = null;

	async function onready() {
		if (targetSelector) {
			for (const node of root.querySelectorAll(targetSelector)) {
				if (node instanceof HTMLElement) {
					const t = /** @type {T} */ (node);
					if ((!predicate || predicate(t))) {
						set.elements.push(t);
					}
				}
			}
		}

		observer = new MutationObserver(entries => {
			try {
				for (const entry of entries) {
					for (const node of entry.addedNodes) {
						//if (selector) // TODO finish updating MonitorSet in response to node changes.
					}
				}
			} catch (err) {
				console.error("Error while monitoring elements", { root, selector }, err);
			}
		});
		observer.observe(root)

		if (callback) {
			callback(set);
		}
	}

	ready(onready);

	await onready();

	return observer;
}

/**
 * Attempts to add an asset to the head element if one matching the `options.search`
 * URL/domain does not already exist in the `<head>` or as a direct child of the `<body>`.
 * 
 * @param {import("./public").AssetInjectionOptions} options
 * @returns {void}
 */
export function injectIfMissing(options) {
	const html = document.querySelector("html") ?? document.documentElement;

	if (html?.getAttribute("data-skip-injections") === "true") {
			return;
	}

	let { type, url, search, attributes } = options;

	const findScript = type === "script";
	const findLink = type === "link";
	if (!findScript && !findLink) {
		globalLogger.warn(`injectIfMissing: 'type' argument is not 'script' or 'link': ${url}`);
		return;
	}

	if (typeof(url) === "string") {
		const maybeUrl = tryConvertToUrl(url);
		if (maybeUrl) {
			url = maybeUrl;
		} else {
			globalLogger.warn(`injectIfMissing: 'url' argument is not a valid URL: ${url}`);
			return;
		}
	}

	if (!search) {
		globalLogger.warn("injectIfMissing: 'search' argument is null or empty.");
		return;
	}
	
	const searchUrl = tryConvertToUrl(search);;
	const searchDomain = searchUrl == null ? search : null;

  for (const el of document.querySelectorAll(`:is(head, body) > ${type}`)) {
		if (!(el instanceof HTMLScriptElement || el instanceof HTMLLinkElement)) continue;
    const target = findScript && el instanceof HTMLScriptElement
			? el.src
			: el instanceof HTMLLinkElement
				? el.href
				: null;
		if (!target) continue;

    if (searchUrl) {
      if (!target.includes(search)) {
				continue;
			}
    } else if (searchDomain) {
      try {
        const elDomain = new URL(target).hostname;
        if (!elDomain.includes(searchDomain)) {
					continue;
				}
      } catch {
        continue;
      }
    }

		return;
	}

	attributes = attributes ?? {};

	const onLoad = () => globalLogger.info(`'${type}' element injected for '${url}'`);

	if (findScript) {
		const el = (/** @type {HTMLScriptElement} */ (document.createElement("script")));
		// @ts-ignore
		el.src = policy.createScriptURL(url.toString());
		el.crossOrigin = attributes.crossorigin || 'anonymous';
		el.type = attributes.type || 'text/javascript';
		el.async = attributes.async ?? false;
		el.defer = attributes.defer ?? false;
		el.addEventListener("load", onLoad);
		for (const key of Object.keys(attributes)) {
			el.setAttribute(key, attributes[key]);
		}
		document.head.appendChild(el);
	}

	if (!findScript) {
		const el = (/** @type {HTMLLinkElement} */ (document.createElement("link")));
		el.rel = 'stylesheet';
		el.href = url.toString();
		el.media = attributes.media || 'all';
		el.addEventListener("load", onLoad);
		for (const key of Object.keys(attributes)) {
			el.setAttribute(key, attributes[key]);
		}
		document.head.appendChild(el);
	}
}

if (browser) {
	Logger.addLogger("global");
	Logger.addLogger("trustedTypes");
}
